import React, { useState } from "react";
import { Grid } from "@mui/material";
import { SEText } from "../../../../../../../components/SEText";
import SEFilterSelect from "../../../../../../../components/SEFilterSelect";
import { RiFilter3Line, RiFilterOffLine } from "react-icons/ri";
import { sortArrayByProp } from "../../../../../../../utils/utils";
import { useQuery } from "react-query";
import { SEDateRangePicker } from "../../../../../../../components/SEDateRangePicker";
import { formatDefaultValueSelect } from "../../../../../../../../utils/mask";
import { AtendimentosSSEService } from "../../../../services";
import { SEButton } from "../../../../../../../components/SEButton";
import { useReportsFilter } from "../../../../../../../../context/ReportsFilterContext";

interface DateRangeValue {
  startDate: string;
  endDate: string;
}

export const Filters = ({ onClearFilters }: { onClearFilters: () => void }) => {
  const {
    bodyFilters,
    handleChangeBodyFilter,
    handleResetBodyFilters,
    filterOptions,
    setFilterOptions,
  } = useReportsFilter();

  const [resetValues, setResetValues] = useState(false);
  const [dateRangeValue, setDateRangeValue] = useState<DateRangeValue>({
    startDate: "",
    endDate: "",
  });
  const abortController = new AbortController() as any;

  const { isLoading, isFetching } = useQuery({
    queryFn: () =>
      AtendimentosSSEService.getFilterOptions(abortController?.signal),
    queryKey: ["getOptionsAtendimentosFilter"],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    onSuccess: setFilterOptions,
  });

  const { isLoading: isLoadingUf } = useQuery<any>({
    queryFn: () =>
      AtendimentosSSEService.getAtendimentosOpcoesPorUf(
        bodyFilters?.ufs?.[0]?.id
      ),
    queryKey: ["getOpcoesPorUfAtendimentosFilter", bodyFilters?.ufs?.[0]?.id],
    refetchOnMount: false,
    enabled: Boolean(bodyFilters?.ufs?.[0]?.id),
    onSuccess: (data) => {
      setFilterOptions((prevState: any) => ({
        ufs: prevState?.ufs,
        anos: prevState?.anos,
        meses: prevState?.meses,
        ...data,
      }));
    },
  });

  const { data: dataFilterSolucoes } = useQuery<any>({
    queryFn: () =>
      AtendimentosSSEService.getFilterSolucoes(
        bodyFilters?.ufs?.[0]?.sigla,
        dateRangeValue.startDate,
        dateRangeValue.endDate
      ),
    queryKey: [
      "getSolucoesFilter",
      bodyFilters?.ufs?.[0]?.sigla,
      dateRangeValue.startDate,
      dateRangeValue.endDate,
    ],
    refetchOnMount: false,
    enabled: Boolean(bodyFilters?.ufs?.[0]?.sigla),
  });

  const { data: dataFilterCanaisDigitais } = useQuery<any>({
    queryFn: () =>
      AtendimentosSSEService.getFilterCanaisDigitais(
        bodyFilters?.ufs?.[0]?.sigla,
        dateRangeValue.startDate,
        dateRangeValue.endDate
      ),
    queryKey: [
      "getCanaisDigitaisFilter",
      bodyFilters?.ufs?.[0]?.sigla,
      dateRangeValue.startDate,
      dateRangeValue.endDate,
    ],
    refetchOnMount: false,
    enabled: Boolean(bodyFilters?.ufs?.[0]?.sigla),
  });

  const { data: dataFilterRegionais, isLoading: isLoadingRegionais } =
    useQuery<any>({
      queryFn: () =>
        AtendimentosSSEService.getFilterRegionais(
          bodyFilters?.ufs?.[0]?.sigla,
          dateRangeValue.startDate,
          dateRangeValue.endDate
        ),
      queryKey: [
        "getRegionaisFilter",
        bodyFilters?.ufs?.[0]?.sigla,
        dateRangeValue.startDate,
        dateRangeValue.endDate,
      ],
      refetchOnMount: false,
      enabled: Boolean(bodyFilters?.ufs?.[0]?.sigla),
    });

  const { data: dataFilterAcoes } = useQuery<any>({
    queryFn: () =>
      AtendimentosSSEService.getFilterAcoes(
        bodyFilters?.ufs?.[0]?.sigla,
        dateRangeValue.startDate,
        dateRangeValue.endDate
      ),
    queryKey: [
      "getAcoesFilter",
      bodyFilters?.ufs?.[0]?.sigla,
      dateRangeValue.startDate,
      dateRangeValue.endDate,
    ],
    refetchOnMount: false,
    enabled: Boolean(bodyFilters?.ufs?.[0]?.sigla),
  });

  const handleCleanFilters = () => {
    onClearFilters();
    handleResetBodyFilters();
    setDateRangeValue({
      startDate: "",
      endDate: "",
    });
    handleChangeBodyFilter("dataInicial", null);
    handleChangeBodyFilter("dataFinal", null);
    setResetValues(true);
    setTimeout(() => {
      setResetValues(false);
    }, 1000);
  };

  const handleRangeDateChange = (value: DateRangeValue) => {
    handleChangeBodyFilter("dataInicial", value.startDate);
    handleChangeBodyFilter("dataFinal", value.endDate);
    setDateRangeValue(value);
  };

  return (
    <Grid>
      <Grid
        item
        flexDirection="row"
        display="flex"
        alignItems="center"
        mr={3}
        mb={2}
      >
        <RiFilter3Line />
        <SEText as="p" size={"medium"} font="Mulish" weight="bold">
          Filtros
        </SEText>
      </Grid>
      <Grid container spacing={2}>
        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            id={"ufs"}
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            label="UF"
            disabled={isLoading || isFetching}
            value={bodyFilters?.ufs?.[0]}
            options={sortArrayByProp(filterOptions?.ufs, "sigla")}
            displayField="sigla"
            onChange={(value) => handleChangeBodyFilter("ufs", [value])}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Todas"
            }
            placeholder={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"cidades"}
            disabled={
              isLoading || isFetching || !bodyFilters?.ufs || isLoadingUf
            }
            label="Cidade"
            value={bodyFilters?.cidades?.[0]}
            options={filterOptions?.cidades}
            displayField="nome"
            onChange={(value) => handleChangeBodyFilter("cidades", [value])}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todos"
            }
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"atendentes"}
            disabled={isLoading || isFetching}
            label="Agente"
            value={bodyFilters?.atendentes}
            options={filterOptions?.atendentes?.map((i: { nome: string }) => {
              return {
                ...i,
                nome: formatDefaultValueSelect(i.nome),
              };
            })}
            onChange={(value) => handleChangeBodyFilter("atendentes", value)}
            displayField="nome"
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"empresaVinculada"}
            label="Empresa habilitada"
            disabled={isLoading || isFetching}
            value={bodyFilters?.empresaVinculada}
            options={filterOptions?.empresasVinculadas}
            onChange={(value) =>
              handleChangeBodyFilter("empresaVinculada", value)
            }
          />
        </Grid>
        <Grid item lg={3} md={2}>
          <SEDateRangePicker
            value={dateRangeValue}
            disabled={bodyFilters?.mes ?? bodyFilters?.ano}
            onChange={handleRangeDateChange}
            reset={resetValues}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            id={"mes"}
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            label="Mês"
            disabled={
              Boolean(dateRangeValue?.startDate || dateRangeValue?.endDate) ||
              isLoading ||
              isFetching
            }
            value={formatDefaultValueSelect(bodyFilters?.mes)}
            options={filterOptions?.meses?.map((i: string) =>
              formatDefaultValueSelect(i)
            )}
            onChange={(value) => {
              handleChangeBodyFilter("mes", value.toUpperCase());
              handleChangeBodyFilter("dataInicial", null);
              handleChangeBodyFilter("dataFinal", null);
            }}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            id={"ano"}
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            label="Ano"
            disabled={
              Boolean(dateRangeValue?.startDate || dateRangeValue?.endDate) ||
              isLoading ||
              isFetching
            }
            value={bodyFilters?.ano}
            options={filterOptions?.anos}
            onChange={(value) => {
              handleChangeBodyFilter("ano", value.toString());
              handleChangeBodyFilter("dataInicial", null);
              handleChangeBodyFilter("dataFinal", null);
            }}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"tema"}
            displayField="descricao"
            label="Tema e subtema"
            disabled={isLoading || isFetching}
            value={bodyFilters?.tema}
            options={filterOptions?.temas}
            onChange={(value) => handleChangeBodyFilter("tema", value)}
          />
        </Grid>
        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            id={"visita"}
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            label="Quantidade de visitas"
            value={bodyFilters?.visita}
            disabled={isLoading || isFetching}
            customLabel={formatDefaultValueSelect}
            options={filterOptions?.visita}
            onChange={(value) => handleChangeBodyFilter("visita", value)}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"portes"}
            label="Porte da empresa"
            disabled={isLoading || isFetching}
            value={bodyFilters?.portes?.[0]}
            options={filterOptions?.portes}
            displayField="sigla"
            onChange={(value) => handleChangeBodyFilter("portes", [value])}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Todos"
            }
            placeholder={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"acao"}
            label="Ações"
            disabled={
              isLoading || isFetching || isLoadingUf || !dataFilterAcoes?.length
            }
            value={bodyFilters?.acao}
            options={dataFilterAcoes}
            customLabel={(value) =>
              value?.descricao ?? value?.id + " - " + value?.nomeAcao
            }
            onChange={(value) => handleChangeBodyFilter("acao", value)}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Todas"
            }
            placeholder={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"regionalProjetoAcao"}
            label="Regionais"
            disabled={
              !dataFilterRegionais ||
              isLoadingRegionais ||
              isLoading ||
              isLoadingUf
            }
            value={bodyFilters?.regionalProjetoAcao}
            options={dataFilterRegionais}
            onChange={(value) =>
              handleChangeBodyFilter("regionalProjetoAcao", value)
            }
          />
        </Grid>
        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            id={"usuariosQue"}
            placeholder={"Selecione uma das opções"}
            disabled={isLoading || isFetching}
            label="Usuários que..."
            value={bodyFilters?.usuariosQue}
            customLabel={formatDefaultValueSelect}
            options={filterOptions?.usuariosQue}
            onChange={(value) => handleChangeBodyFilter("usuariosQue", value)}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Todas"
            }
            placeholder={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"solucao"}
            label="Soluções Sebrae"
            disabled={
              isLoading ||
              isFetching ||
              isLoadingUf ||
              !dataFilterSolucoes?.length
            }
            value={bodyFilters?.solucao}
            options={dataFilterSolucoes}
            onChange={(value) => handleChangeBodyFilter("solucao", value)}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Todos"
            }
            placeholder={
              !bodyFilters?.ufs
                ? "Selecione uma UF"
                : isLoading || isFetching || isLoadingUf
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"canalDigital"}
            label="Presença digital"
            value={bodyFilters?.canalDigital}
            disabled={
              isLoading ||
              isFetching ||
              isLoadingUf ||
              !dataFilterCanaisDigitais?.length
            }
            options={dataFilterCanaisDigitais}
            onChange={(value) => handleChangeBodyFilter("canalDigital", value)}
          />
        </Grid>

        <Grid item lg={3} md={2}>
          <SEFilterSelect
            defaultOption={
              isLoading || isFetching ? "Carregando opções..." : "Todas"
            }
            placeholder={
              isLoading || isFetching
                ? "Carregando opções..."
                : "Selecione uma das opções"
            }
            id={"interacao"}
            label="Interação"
            customLabel={formatDefaultValueSelect}
            disabled={isLoading || isFetching}
            value={bodyFilters?.interacao}
            options={filterOptions?.tiposInteracao}
            onChange={(value) => handleChangeBodyFilter("interacao", value)}
          />
        </Grid>
      </Grid>
      <Grid item marginY={3.5}>
        <SEButton
          onClick={handleCleanFilters}
          textStyle={{ fontWeight: "bold" }}
        >
          <RiFilterOffLine size={20} style={{ marginRight: 4 }} /> Limpar
          filtros
        </SEButton>
      </Grid>
    </Grid>
  );
};
